import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatMoney, STATUS_META, formatDate } from "@/lib/format";
import { FileText, Users, Ship, TrendingUp, Plus, ArrowUpRight, Mail, Archive, UserPlus, FilePlus2, Activity, LogIn, Download, ListTodo, Trash2, CalendarDays, Hammer, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function TasksPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tasks, setTasks] = useState([]);
  const [text, setText] = useState("");
  const [due, setDue] = useState("");
  const [assignee, setAssignee] = useState("");
  const [staff, setStaff] = useState([]);

  const load = () => api.get("/tasks").then(({ data }) => setTasks(data)).catch(() => {});
  useEffect(() => {
    load();
    if (isAdmin) api.get("/staff").then(({ data }) => setStaff(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addTask = async () => {
    if (!text.trim()) return;
    try {
      await api.post("/tasks", {
        text: text.trim(),
        due_date: due || null,
        assignee_id: assignee || null,
      });
      setText("");
      setDue("");
      const assignedName = staff.find((s) => s.id === assignee)?.name;
      if (assignee && assignee !== user?.id && assignedName) toast.success(`Task added to ${assignedName}'s list`);
      setAssignee("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add task");
    }
  };

  const toggle = (t) =>
    api.patch(`/tasks/${t.id}`, { done: !t.done }).then(load).catch(() => toast.error("Could not update task"));
  const remove = (t) => api.delete(`/tasks/${t.id}`).then(load).catch(() => toast.error("Could not delete task"));
  const overdue = (t) => t.due_date && !t.done && t.due_date < new Date().toISOString().slice(0, 10);

  return (
    <div className="sos-surface rounded-lg mb-6" data-testid="tasks-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#DDE5EC]">
        <div className="flex items-center gap-2">
          <ListTodo className="h-4 w-4 text-[#0088CC]" />
          <div className="font-display text-lg font-semibold text-[#011836]">My Tasks</div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#5B6B7C]">
          {tasks.filter((t) => !t.done).length} open
        </div>
      </div>
      <div className="px-5 py-3 border-b border-[#DDE5EC] flex flex-wrap items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addTask()}
          placeholder="Add a task…"
          className="flex-1 min-w-[200px] h-8 rounded bg-white border border-[#DDE5EC] px-2.5 text-xs text-[#011836] placeholder:text-[#64748B] focus:outline-none focus:border-[#0088CC]"
          data-testid="task-text-input"
        />
        <input
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          className="h-8 rounded bg-white border border-[#DDE5EC] px-2 text-xs text-[#33475C] focus:outline-none focus:border-[#0088CC]"
          data-testid="task-due-input"
        />
        {isAdmin && staff.length > 0 && (
          <select
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            className="h-8 rounded bg-white border border-[#DDE5EC] px-2 text-xs text-[#33475C] focus:outline-none focus:border-[#0088CC]"
            data-testid="task-assignee-select"
          >
            <option value="">My list</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || s.email}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={addTask}
          className="h-8 px-3 rounded bg-[#0088CC] hover:bg-[#006DA6] text-white text-xs inline-flex items-center gap-1"
          data-testid="task-add-btn"
        >
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>
      <div className="px-5 py-3 space-y-1.5 max-h-72 overflow-y-auto">
        {tasks.length === 0 && <div className="text-xs text-[#64748B]">No tasks — enjoy the calm seas.</div>}
        {tasks.map((t, i) => (
          <div key={t.id} className="flex items-center gap-2.5 group" data-testid={`task-row-${i}`}>
            <input
              type="checkbox"
              checked={t.done}
              onChange={() => toggle(t)}
              className="h-3.5 w-3.5 accent-[#0088CC] shrink-0 cursor-pointer"
              data-testid={`task-check-${i}`}
            />
            <span className={`flex-1 text-xs truncate ${t.done ? "line-through text-[#64748B]" : "text-[#011836]"}`}>
              {t.quote_id ? (
                <Link to={`/quotes/${t.quote_id}`} className="hover:text-[#0088CC]">
                  {t.text}
                </Link>
              ) : (
                t.text
              )}
              {t.created_by_name && t.created_by_name !== (user?.name || "") && (
                <span className="text-[#64748B]"> · from {t.created_by_name}</span>
              )}
            </span>
            {t.due_date && (
              <span className={`inline-flex items-center gap-1 text-[10px] whitespace-nowrap ${overdue(t) ? "text-red-400" : "text-[#5B6B7C]"}`}>
                <CalendarDays className="h-3 w-3" /> {t.due_date}
              </span>
            )}
            <button
              type="button"
              onClick={() => remove(t)}
              className="text-[#64748B] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              data-testid={`task-delete-${i}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function ActivityCol({ title, icon: Icon, rows, kind }) {
  return (
    <div className="flex-1 min-w-[220px]" data-testid={`activity-${kind}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#5B6B7C] mb-2">
        <Icon className="h-3 w-3 text-[#0088CC]" /> {title}
      </div>
      {(!rows || rows.length === 0) && (
        <div className="text-xs text-[#64748B]">No activity in this period.</div>
      )}
      <div className="space-y-2">
        {(rows || []).map((r, i) => (
          <div key={i} className="text-xs" data-testid={`activity-${kind}-row-${i}`}>
            <span className={r.role === "client" ? "text-[#F5C242] font-semibold" : "text-[#011836] font-medium"}>
              {r.who}
            </span>
            <span className={r.role === "client" ? "text-[#F5C242]/70" : "text-[#64748B]"}>
              {" "}({r.role === "client" ? "CLIENT" : r.role})
            </span>
            <div className="text-[11px] text-[#5B6B7C] mt-0.5">
              {kind === "logins"
                ? `${r.count} login${r.count !== 1 ? "s" : ""} · last ${formatTime(r.last)}`
                : (r.vessels || [])
                    .map((v) => (v.count > 1 ? `${v.name} ×${v.count}` : v.name))
                    .join(", ")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCell({ label, value, accent = false, testid }) {
  const str = typeof value === "string" || typeof value === "number" ? String(value) : "";
  const long = str.length > 16;
  return (
    <div
      className="sos-surface rounded-lg p-5 sos-surface-hover min-w-0"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#5B6B7C]">{label}</div>
      <div
        className={`mt-3 font-display font-bold break-words leading-tight ${
          long ? "text-base sm:text-lg lg:text-xl" : "text-2xl sm:text-3xl"
        } ${accent ? "text-[#0088CC]" : "text-[#011836]"}`}
      >
        {value}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityHours, setActivityHours] = useState(24);
  const [sendingReport, setSendingReport] = useState(false);

  const sendActivityReport = async () => {
    setSendingReport(true);
    try {
      const { data } = await api.post("/reports/activity/send");
      if (data.error) toast.error(data.error);
      else toast.success(`Report emailed — ${data.users_covered ?? 0} user${(data.users_covered ?? 0) !== 1 ? "s" : ""} covered`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send the report");
    } finally {
      setSendingReport(false);
    }
  };
  const [loading, setLoading] = useState(true);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [newSignups, setNewSignups] = useState([]);
  const [pendingRegs, setPendingRegs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tenders, setTenders] = useState([]);
  const [convertingId, setConvertingId] = useState(null);

  // Registrations still waiting on an approve/decline decision — admins
  // see a prominent banner so requests never sit unnoticed.
  const loadPendingRegs = async () => {
    try {
      const { data } = await api.get("/registrations/pending");
      setPendingRegs(data);
    } catch {
      /* admin-only — staff/clients silently get nothing */
    }
  };

  const loadQuoteRequests = async () => {
    try {
      const { data } = await api.get("/quote-requests");
      // Only show items still on the inbox — archived ones drop off the
      // dashboard but are still in the DB if needed for reporting later.
      setQuoteRequests(data.filter((r) => r.status !== "archived"));
    } catch {
      /* Non-admins won't hit this branch (Dashboard is admin-only) */
    }
  };

  // Recent self-signups so the admin can spot fresh client accounts that
  // came in via the public /register link.
  const loadNewSignups = async () => {
    try {
      const { data } = await api.get("/admin/new-signups");
      setNewSignups(data);
    } catch {
      /* admin-only — staff/clients silently get nothing */
    }
  };

  useEffect(() => {
    let active = true;
    // .catch swallows the rejection so view-only staff (who may lack the
    // 'dashboard' permission and get a 403) don't trigger an unhandled
    // promise warning. The JSX renders gracefully with ``stats`` null
    // (everything falls back to em-dashes / zero).
    api
      .get("/dashboard/stats")
      .then(({ data }) => active && setStats(data))
      .catch(() => {})
      .finally(() => active && setLoading(false));
    loadQuoteRequests();
    loadNewSignups();
    loadPendingRegs();
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    api.get("/tenders").then(({ data }) => setTenders(data)).catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    api
      .get(`/activity/summary?hours=${activityHours}`)
      .then(({ data }) => active && setActivity(data))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [activityHours]);

  const archiveRequest = async (req) => {
    try {
      await api.patch(`/quote-requests/${req.id}`, { status: "archived" });
      setQuoteRequests((cur) => cur.filter((r) => r.id !== req.id));
      toast.success("Enquiry archived");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not archive");
    }
  };

  // Turn an incoming enquiry into a pre-populated draft quote (vessel
  // linked, client attached/created, enquiry message dropped into the
  // quote notes). Sends the salesperson straight to the editor.
  const convertToQuote = async (req) => {
    setConvertingId(req.id);
    try {
      const { data } = await api.post(`/quote-requests/${req.id}/convert`);
      setQuoteRequests((cur) => cur.filter((r) => r.id !== req.id));
      toast.success(
        data.already_converted
          ? `Enquiry was already converted to ${data.quote_number || "a quote"}`
          : `Draft quote ${data.quote_number || ""} created`,
      );
      navigate(`/quotes/${data.quote_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create quote");
      setConvertingId(null);
    }
  };

  const newRequestCount = quoteRequests.filter((r) => r.status === "new").length;

  const pipelineByCurrency = stats?.by_currency || {};
  const currencies = Object.keys(pipelineByCurrency);

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1400px] mx-auto pb-24">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#0088CC] mb-2">
            Control Room
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#011836]">
            Dashboard
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Pipeline, performance, and recent activity across all quotes.
          </p>
        </div>
        <Link
          to="/quotes/new"
          data-testid="new-quote-btn-dashboard"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0088CC] hover:bg-[#006DA6] text-white rounded-md font-display tracking-wide text-sm transition-colors"
        >
          <Plus className="h-4 w-4" /> New Quote
        </Link>
      </div>

      {/* Pending registration approvals — impossible to miss */}
      {pendingRegs.length > 0 && (
        <Link
          to="/staff"
          className="flex items-center justify-between gap-3 mb-6 rounded-lg border border-amber-300 bg-amber-50 px-5 py-3.5 hover:bg-amber-100 transition-colors"
          data-testid="pending-registrations-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-500 text-white text-xs font-bold shrink-0" data-testid="pending-registrations-count">
              {pendingRegs.length}
            </span>
            <div className="min-w-0">
              <span className="text-sm font-semibold text-[#7C4A03]">
                {pendingRegs.length === 1 ? "1 registration awaiting your approval" : `${pendingRegs.length} registrations awaiting your approval`}
              </span>
              <span className="hidden sm:inline text-sm text-[#9A6B1F]">
                {" — "}
                {pendingRegs.slice(0, 3).map((r) => r.name || r.email).join(", ")}
                {pendingRegs.length > 3 ? "…" : ""}
              </span>
            </div>
          </div>
          <span className="text-xs font-semibold text-[#B45309] whitespace-nowrap">Review →</span>
        </Link>
      )}

      {/* Primary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCell
          label="Total Quotes"
          value={loading ? "—" : stats?.total_quotes ?? 0}
          testid="kpi-total-quotes"
        />
        <StatCell
          label="Pipeline Value"
          accent
          value={
            loading
              ? "—"
              : currencies.length === 0
              ? formatMoney(0, "AUD")
              : currencies
                  .map((c) => formatMoney(pipelineByCurrency[c].pipeline, c))
                  .join(" · ")
          }
          testid="kpi-pipeline"
        />
        <StatCell
          label="Won Value"
          value={
            loading
              ? "—"
              : currencies.length === 0
              ? formatMoney(0, "AUD")
              : currencies
                  .map((c) => formatMoney(pipelineByCurrency[c].accepted, c))
                  .join(" · ")
          }
          testid="kpi-won"
        />
        <StatCell
          label="Conversion Rate"
          value={loading ? "—" : `${stats?.conversion_rate ?? 0}%`}
          testid="kpi-conversion"
        />
      </div>

      {/* Status breakdown + counts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="sos-surface rounded-lg p-5 lg:col-span-2" data-testid="status-breakdown">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#5B6B7C]">
              Status Breakdown
            </div>
            <TrendingUp className="h-4 w-4 text-[#0088CC]" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {["draft", "sent", "accepted", "rejected"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => navigate(`/quotes?status=${s}`)}
                className="rounded-md border border-[#DDE5EC] bg-white p-4 text-left transition-colors hover:border-[#0088CC] hover:bg-[#F4FAFD] cursor-pointer"
                data-testid={`status-cell-${s}`}
              >
                <div className={`text-xs px-2 py-0.5 rounded inline-block ${STATUS_META[s].className}`}>
                  {STATUS_META[s].label}
                </div>
                <div className="mt-3 font-display text-3xl font-bold text-[#011836]">
                  {stats?.status_counts?.[s] ?? 0}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
          <Link
            to="/clients"
            className="sos-surface rounded-lg p-5 sos-surface-hover flex items-start gap-3"
            data-testid="kpi-clients"
          >
            <div className="p-2 rounded bg-[#E6F2FA] border border-[#0088CC]/40">
              <Users className="h-4 w-4 text-[#0088CC]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#5B6B7C]">Clients</div>
              <div className="font-display text-2xl font-bold text-[#011836] mt-1">
                {stats?.clients_count ?? 0}
              </div>
            </div>
          </Link>
          <Link
            to="/vessels"
            className="sos-surface rounded-lg p-5 sos-surface-hover flex items-start gap-3"
            data-testid="kpi-vessels"
          >
            <div className="p-2 rounded bg-[#E6F2FA] border border-[#0088CC]/40">
              <Ship className="h-4 w-4 text-[#0088CC]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#5B6B7C]">Vessels</div>
              <div className="font-display text-2xl font-bold text-[#011836] mt-1">
                {stats?.vessels_count ?? 0}
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* New signups this week — fresh client self-registrations via /register */}
      {newSignups.length > 0 && (
        <div className="sos-surface rounded-lg mb-6" data-testid="new-signups-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#DDE5EC]">
            <div className="flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-[#0088CC]" />
              <div className="font-display text-lg font-semibold text-[#011836]">
                New signups this week
              </div>
              <span
                className="text-[10px] uppercase tracking-wider text-[#0b0e12] bg-[#0088CC] px-2 py-0.5 rounded-full font-display font-bold"
                data-testid="new-signups-count"
              >
                {newSignups.length}
              </span>
            </div>
            <Link
              to="/staff"
              className="text-[11px] uppercase tracking-wider text-[#0088CC] hover:text-[#011836] inline-flex items-center gap-1"
            >
              Manage users <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="divide-y divide-[#DDE5EC]">
            {newSignups.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between gap-4 px-5 py-3"
                data-testid={`new-signup-${u.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#011836] font-medium truncate">
                    {u.name || u.email}
                    {u.company && (
                      <span className="text-[#5B6B7C] font-normal"> · {u.company}</span>
                    )}
                  </div>
                  <div className="text-xs text-[#5B6B7C] truncate">
                    <a
                      href={`mailto:${u.email}`}
                      className="hover:text-[#0088CC]"
                    >
                      {u.email}
                    </a>
                    {u.phone && <span> · {u.phone}</span>}
                  </div>
                </div>
                <div className="text-[11px] text-[#64748B] tabular-nums shrink-0">
                  {formatDate(u.created_at)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Client enquiries — leads from the public catalogue */}
      {quoteRequests.length > 0 && (
        <div className="sos-surface rounded-lg mb-6" data-testid="client-enquiries-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#DDE5EC]">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-[#0088CC]" />
              <div className="font-display text-lg font-semibold text-[#011836]">Client Enquiries</div>
              {newRequestCount > 0 && (
                <span
                  className="ml-2 text-[10px] uppercase tracking-wider font-display font-semibold px-2 py-0.5 rounded bg-amber-500/20 text-amber-700 border border-amber-400/40"
                  data-testid="new-enquiry-count"
                >
                  {newRequestCount} new
                </span>
              )}
            </div>
          </div>
          <div className="divide-y divide-[#DDE5EC]">
            {quoteRequests.map((r) => (
              <div
                key={r.id}
                className="px-5 py-4 hover:bg-white transition-colors"
                data-testid={`enquiry-row-${r.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="font-display text-sm font-semibold text-[#011836]">
                        {r.vessel_name}
                      </span>
                      {r.status === "new" && (
                        <span className="text-[9px] uppercase tracking-wider font-display font-semibold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-700 border border-amber-400/40">
                          New
                        </span>
                      )}
                      <span className="text-xs text-[#5B6B7C]">
                        {formatDate(r.created_at)}
                      </span>
                    </div>
                    <div className="text-xs text-[#5B6B7C] mt-1.5">
                      <span className="text-[#011836]">{r.client_name}</span>
                      {r.client_company && (
                        <>
                          <span className="mx-1.5 text-[#475569]">·</span>
                          {r.client_company}
                        </>
                      )}
                      <span className="mx-1.5 text-[#475569]">·</span>
                      <a
                        href={`mailto:${r.client_email}`}
                        className="text-[#0088CC] hover:underline"
                      >
                        {r.client_email}
                      </a>
                      {r.client_phone && (
                        <>
                          <span className="mx-1.5 text-[#475569]">·</span>
                          {r.client_phone}
                        </>
                      )}
                    </div>
                    {r.message && (
                      <div className="mt-2 text-sm text-[#33475C] whitespace-pre-line">
                        {r.message}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => convertToQuote(r)}
                      disabled={convertingId === r.id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs bg-[#0088CC] hover:bg-[#006DA6] text-white font-semibold disabled:opacity-60"
                      data-testid={`convert-enquiry-${r.id}`}
                      title="Create a pre-populated draft quote from this enquiry"
                    >
                      <FilePlus2 className="h-3.5 w-3.5" />
                      {convertingId === r.id ? "Creating…" : "Create quote"}
                    </button>
                    <button
                      onClick={() => archiveRequest(r)}
                      className="p-1.5 rounded text-[#5B6B7C] hover:text-[#011836] hover:bg-[#EDF1F5]"
                      data-testid={`archive-enquiry-${r.id}`}
                      title="Archive this enquiry"
                    >
                      <Archive className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming tender dates — every future key date / requirement pulled
          from the open tenders, soonest first, linking to the tender. */}
      {(() => {
        const today = new Date().toISOString().slice(0, 10);
        const entries = [];
        for (const t of tenders) {
          if (["won", "lost", "withdrawn"].includes(t.status)) continue;
          const kds = (t.key_dates || []).filter((k) => (k.date || "").slice(0, 10) >= today);
          for (const k of kds) {
            let kdTime = (k.time_text || "").trim();
            if (!kdTime) {
              // Derive from an ISO datetime verbatim (document-local time),
              // never converted to the viewer's clock.
              const m = (k.date || "").match(/T(\d{2}):(\d{2})/);
              if (m) {
                const h = Number(m[1]);
                const off = ((k.date || "").match(/([+-]\d{2}:?\d{2}|Z)\s*$/) || [])[1];
                kdTime = `${h % 12 || 12}:${m[2]} ${h >= 12 ? "pm" : "am"}${off ? ` (UTC${off === "Z" ? "+00:00" : off})` : ""}`;
              }
            }
            entries.push({
              tenderId: t.id,
              tenderTitle: t.title,
              date: (k.date || "").slice(0, 10),
              time: kdTime,
              label: k.label || "Key date",
              mandatory: k.mandatory !== false,
              location: k.location || "",
              req: k.register_instructions || k.notes || "",
            });
          }
          if (t.due_date && t.due_date >= today && !kds.some((k) => (k.date || "").slice(0, 10) === t.due_date)) {
            entries.push({
              tenderId: t.id,
              tenderTitle: t.title,
              date: t.due_date,
              label: "Tender lodgement due",
              mandatory: true,
              location: "",
              req: "",
            });
          }
        }
        entries.sort((a, b) => a.date.localeCompare(b.date));
        if (!entries.length) return null;
        const fmtD = (d) => {
          try {
            return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
          } catch {
            return d;
          }
        };
        const daysTo = (d) => Math.round((new Date(`${d}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
        return (
          <div className="sos-surface rounded-lg mb-6" data-testid="tender-dates-panel">
            <div className="px-5 py-4 border-b border-[#DDE5EC] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-[#0088CC]" />
                <div className="font-display text-lg font-semibold text-[#011836]">Upcoming tender dates</div>
                <span className="text-[11px] text-[#5B6B7C]">{entries.length} date{entries.length > 1 ? "s" : ""}</span>
              </div>
              <Link to="/tenders" className="text-xs text-[#0088CC] hover:underline inline-flex items-center gap-1" data-testid="tender-dates-view-all">
                All tenders <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="divide-y divide-[#E8EEF3] max-h-80 overflow-y-auto">
              {entries.slice(0, 10).map((e, i) => {
                const d = daysTo(e.date);
                return (
                  <Link
                    key={`${e.tenderId}-${i}`}
                    to={`/tenders?focus=${e.tenderId}`}
                    className="flex items-start gap-3 px-5 py-2.5 hover:bg-[#F4F8FB] transition-colors"
                    data-testid={`tender-date-row-${e.tenderId}-${i}`}
                  >
                    <div className="w-24 shrink-0">
                      <div className={`text-xs font-display font-semibold ${d <= 7 ? "text-[#D97706]" : "text-[#011836]"}`}>{fmtD(e.date)}</div>
                      {e.time && <div className="text-[10px] font-semibold text-[#0088CC]">{e.time}</div>}
                      <div className="text-[10px] text-[#64748B]">{d === 0 ? "today" : d === 1 ? "tomorrow" : `in ${d}d`}</div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-[#011836] font-medium">
                        {e.mandatory && (
                          <span className="inline-block text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 mr-1.5 align-middle">MANDATORY</span>
                        )}
                        {e.label}
                        {e.location ? <span className="text-[#5B6B7C]"> · {e.location}</span> : null}
                      </div>
                      {e.req && <div className="text-[10px] text-[#64748B] mt-0.5 truncate">{e.req}</div>}
                      <div className="text-[10px] text-[#0088CC] mt-0.5 truncate">{e.tenderTitle}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
            {entries.length > 10 && (
              <div className="px-5 py-2 text-[10px] text-[#64748B] border-t border-[#E8EEF3]">+{entries.length - 10} more on the Tenders page</div>
            )}
          </div>
        );
      })()}

      {/* Projects overview — open issues + next delivery at a glance */}
      {projects.length > 0 && (() => {
        const active = projects.filter((pr) => pr.status !== "delivered");
        const openIssues = active.reduce((s, pr) => s + (pr.issues || []).filter((i) => i.status === "open").length, 0);
        const upcoming = active.filter((pr) => pr.delivery_date).sort((a, b) => a.delivery_date.localeCompare(b.delivery_date));
        const next = upcoming[0];
        const today = new Date().toISOString().slice(0, 10);
        const isReportOverdue = (pr) =>
          pr.next_report_date &&
          pr.next_report_date < today &&
          !(pr.reports || []).some((r) => (r.uploaded_at || "").slice(0, 10) >= pr.next_report_date);
        const overdueCount = active.filter(isReportOverdue).length;
        const rows = [...active].sort((a, b) => (a.delivery_date || "9999").localeCompare(b.delivery_date || "9999")).slice(0, 5);
        return (
          <div className="sos-surface rounded-lg mb-6" data-testid="projects-panel">
            <div className="px-5 py-4 border-b border-[#DDE5EC] flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Hammer className="h-4 w-4 text-[#0088CC]" />
                <div className="font-display text-lg font-semibold text-[#011836]">Projects</div>
                {openIssues > 0 && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid="projects-open-issues">
                    <AlertTriangle className="h-3 w-3" /> {openIssues} open issue{openIssues > 1 ? "s" : ""}
                  </span>
                )}
                {overdueCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid="projects-reports-overdue">
                    <AlertTriangle className="h-3 w-3" /> {overdueCount} report{overdueCount > 1 ? "s" : ""} overdue
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {next && (
                  <span className="text-xs text-[#5B6B7C]" data-testid="projects-next-delivery">
                    Next delivery: <span className="font-semibold text-[#011836]">{next.name}</span> · {formatDate(next.delivery_date)}
                  </span>
                )}
                <Link to="/projects" className="text-xs text-[#0088CC] hover:underline" data-testid="projects-panel-all">All projects →</Link>
              </div>
            </div>
            <div className="divide-y divide-[#F1F5F9]">
              {rows.map((pr) => {
                const open = (pr.issues || []).filter((i) => i.status === "open").length;
                return (
                  <Link key={pr.id} to={`/projects/${pr.id}`} className="flex items-center gap-4 px-5 py-3 hover:bg-[#F4FAFD] transition-colors" data-testid={`projects-panel-row-${pr.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-[#011836] truncate flex items-center gap-2">
                        <span className="truncate">{pr.name}</span>
                        {isReportOverdue(pr) && (
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 whitespace-nowrap" data-testid={`project-report-overdue-${pr.id}`}>
                            <AlertTriangle className="h-2.5 w-2.5" /> Report due {formatDate(pr.next_report_date)}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#5B6B7C] truncate">{pr.client_name || "No client"}{pr.vessel_name ? ` · ${pr.vessel_name}` : ""}</div>
                    </div>
                    <div className="hidden sm:block w-28">
                      <div className="h-1.5 rounded-full bg-[#E8EEF3] overflow-hidden">
                        <div className="h-full rounded-full bg-[#0088CC]" style={{ width: `${pr.progress}%` }} />
                      </div>
                      <div className="text-[10px] text-[#5B6B7C] mt-0.5 text-right">{pr.progress}%</div>
                    </div>
                    <div className="text-xs text-[#5B6B7C] w-28 text-right whitespace-nowrap">
                      {pr.delivery_date ? formatDate(pr.delivery_date) : "No date"}
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full w-20 text-center ${open > 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {open > 0 ? `${open} issue${open > 1 ? "s" : ""}` : "Clear"}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Staff task list */}
      <TasksPanel />

      {/* Activity panel — last 24h logins / vessel views / flyer downloads */}
      {activity && (
        <div className="sos-surface rounded-lg mb-6" data-testid="activity-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#DDE5EC]">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-[#0088CC]" />
              <div className="font-display text-lg font-semibold text-[#011836]">Activity</div>
            </div>
            <div className="flex items-center gap-1.5">
              {[
                [24, "24 hours"],
                [168, "7 days"],
              ].map(([h, label]) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setActivityHours(h)}
                  className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest border transition-colors ${
                    activityHours === h
                      ? "bg-[#0088CC] border-[#0088CC] text-white"
                      : "border-[#DDE5EC] text-[#5B6B7C] hover:border-[#C4D0DB] hover:text-[#011836]"
                  }`}
                  data-testid={`activity-range-${h}`}
                >
                  {label}
                </button>
              ))}
              <button
                type="button"
                onClick={sendActivityReport}
                disabled={sendingReport}
                className="ml-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest border border-[#0088CC]/50 text-[#0088CC] hover:bg-[#E6F2FA] hover:text-[#011836] transition-colors disabled:opacity-50"
                data-testid="send-activity-report-btn"
                title="Email the weekly activity report to admins now"
              >
                <Mail className="h-3 w-3" />
                {sendingReport ? "Sending…" : "Send report now"}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-8 px-5 py-4">
            <ActivityCol title="Logins" icon={LogIn} rows={activity.logins} kind="logins" />
            <ActivityCol title="Vessel pages viewed" icon={Ship} rows={activity.vessel_views} kind="vessel-views" />
            <ActivityCol title="Flyers downloaded" icon={Download} rows={activity.flyer_downloads} kind="flyer-downloads" />
          </div>
        </div>
      )}

      {/* Recent quotes */}
      <div className="sos-surface rounded-lg" data-testid="recent-quotes">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#DDE5EC]">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-[#0088CC]" />
            <div className="font-display text-lg font-semibold text-[#011836]">Recent Quotes</div>
          </div>
          <Link
            to="/quotes"
            className="text-xs uppercase tracking-wider text-[#0088CC] hover:text-[#011836] inline-flex items-center gap-1"
            data-testid="see-all-quotes"
          >
            See all <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="divide-y divide-[#DDE5EC]">
          {(!stats?.recent_quotes || stats.recent_quotes.length === 0) && (
            <div className="px-5 py-10 text-center text-sm text-[#5B6B7C]">
              No quotes yet. Create your first quote to get started.
            </div>
          )}
          {stats?.recent_quotes?.map((q) => (
            <Link
              key={q.id}
              to={`/quotes/${q.id}`}
              className="flex items-center justify-between px-5 py-3.5 hover:bg-white transition-colors"
              data-testid={`recent-quote-${q.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3">
                  <span className="font-display text-sm font-semibold text-[#0088CC] shrink-0">
                    {q.number}
                  </span>
                  <span className="text-sm text-[#011836] truncate">{q.title}</span>
                </div>
                <div className="text-xs text-[#5B6B7C] mt-1">{formatDate(q.created_at)}</div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded ${STATUS_META[q.status]?.className || ""}`}>
                  {STATUS_META[q.status]?.label || q.status}
                </span>
                <span className="font-display font-semibold text-[#011836] tabular-nums">
                  {formatMoney(q.total, q.currency)}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
